import React, { useEffect, useRef, useState } from "react";
import "./Greeting.css";

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

// ─── useInView ───────────────────────────────────────────────────────
function useInView(rootMargin = "0px 0px -60px 0px") {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.unobserve(el); } },
      { threshold: 0, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return [ref, inView];
}

// ─── 本文データ ───────────────────────────────────────────────────────
const BLOCKS = [
  {
    id: "origin", num: "— 01",
    texts: [
      <>
        長きにわたり「全員で協力してものづくりを行う」という精神のもと、
        プラスチック製品の開発・製造に取り組んでまいりました。
        この精神は、新しい社名「<span className="g-zenkyo-light">ZENKYO</span>」にも引き継がれ、
        これからも当社の原点として大切にしてまいります。
      </>,
    ],
  },
  {
    id: "products", num: "— 02",
    texts: [
      <>
        当社は創業以来、プラスチックの特性を最大限に活かした製品開発を行い、
        耐食性を活かしたシリコンウェーハ洗浄装置や各種エッチング装置など、
        多岐にわたる製品を皆さまにご提供してまいりました。
      </>,
    ],
  },
  {
    id: "future", num: "— 03",
    texts: [
      <>今後は、これまで培ってきた技術と実績、そして情報をさらに未来に向けて発展させてまいります。</>,
      <>
        まず第一に、現行製品の技術を整理・完成させ、既存の製品をより上質で効率的なものへ進化させます。
        パーティクルの低減や生産効率の向上を追求し、より完全な製品をお届けできるよう努めてまいります。
      </>,
      <>
        第二に、新規製品の開発を推進します。ウェハ洗浄装置のみならず、酸化ガリウムやSiC、窒化物などの
        パワーデバイス向けエッチング装置や、医療・介護分野における多様な機械装置など、
        当社のノウハウを活かした製品開発を進めます。
      </>,
      <>
        第三に、最新技術の導入に注力します。環境に配慮した新たな洗浄技術や高効率・低薬液のクリーンな
        洗浄方法など、従来の技術の枠を超えた革新を追求し、皆さまにより良い価値をご提供してまいります。
      </>,
    ],
  },
  {
    id: "closing", num: "— 04",
    texts: [
      <>
        <span className="g-zenkyo-light">ZENKYO</span>は、これからも半導体洗浄装置のリーディングカンパニーとして
        信頼いただけるよう、たゆまぬ努力を続けてまいります。
        皆さまの変わらぬご理解とご支援を賜りますよう、お願い申し上げます。
      </>,
    ],
  },
];

// ─── AnimatedBlock ────────────────────────────────────────────────────
function AnimatedBlock({ id, num, texts }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className="gr-block"
      style={{
        opacity:    inView ? 1 : 0,
        transform:  inView ? "translateY(0)" : "translateY(22px)",
        transition: `opacity 1s ${EASE}, transform 1s ${EASE}`,
      }}
    >
      <span className="gr-block-num">{num}</span>
      {texts.map((text, j) => (
        <p key={j} style={{
          opacity:         inView ? 1 : 0,
          transform:       inView ? "translateY(0)" : "translateY(10px)",
          transition:      `opacity 0.9s ${EASE}, transform 0.9s ${EASE}`,
          transitionDelay: inView ? `${j * 0.13 + 0.1}s` : "0s",
        }}>
          {text}
        </p>
      ))}
    </div>
  );
}

// ─── AnimatedSignature ────────────────────────────────────────────────
function AnimatedSignature() {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className="gr-signature-wrap" style={{
      opacity:    inView ? 1 : 0,
      transform:  inView ? "translateY(0)" : "translateY(18px)",
      transition: `opacity 1s ${EASE} 0.15s, transform 1s ${EASE} 0.15s`,
    }}>
      <span className="gr-signature-role">Representative Director</span>
      <span className="gr-signature-company">
        株式会社<span className="g-zenkyo-light"> ZENKYO</span>
      </span>
      <span className="gr-signature-name">安藤　肇</span>
    </div>
  );
}

// ─── メインコンポーネント ─────────────────────────────────────────────
export default function Greeting() {
  return (
    <>
      <div id="page-top" />

      {/* ═══ HERO ═══ */}
      <section className="gr-hero">
        <div className="gr-hero-bg" aria-hidden="true" />
        <div className="gr-hero-vline-r" aria-hidden="true" />

        <div className="gr-hero-content">
          <span className="gr-hero-accent" aria-hidden="true" />
          <span className="gr-hero-eyebrow">Message from the President</span>
          <h1 className="gr-hero-title">
            当社はこのたび創業60年の節目を迎え<br />
            <span className="gr-accent-word">ZENKYO</span>へ商号を変更いたしました。
          </h1>
        </div>

        <div className="gr-hero-bottom" aria-hidden="true">
          <span className="gr-hero-scroll-line" />
          <span className="gr-hero-scroll-text">scroll</span>
        </div>
      </section>

      {/* ═══ BODY ═══ */}
      <section className="gr-body">
        <div className="gr-content">

          {/* 左サイドバー */}
          <aside className="gr-sidebar" aria-hidden="true">
            <span className="gr-sidebar-label">Greeting</span>
            <span className="gr-sidebar-num">ご<br />挨<br />拶</span>
          </aside>

          {/* 本文 */}
          <main className="gr-main">
            {BLOCKS.map(({ id, num, texts }) => (
              <AnimatedBlock key={id} id={id} num={num} texts={texts} />
            ))}
            <AnimatedSignature />
          </main>

        </div>
      </section>
    </>
  );
}